// ============================================
// Auth Hooks
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore, type AuthUser } from '../stores/auth';
import { useTenantStore } from '../stores/tenant';
import { api, ApiError } from '../lib/api';

let completedBootstrapKey: string | null = null;
let inFlightBootstrap: { key: string; promise: Promise<void> } | null = null;

function getBootstrapKey(user: AuthUser, token: string | null): string {
  return `${user.id}:${user.role}:${token ?? ''}`;
}

function resetBootstrapGuard(): void {
  completedBootstrapKey = null;
  inFlightBootstrap = null;
}

type BootstrapTenant = {
  id: string;
  name: string;
  isActive?: boolean;
  settings?: Record<string, unknown>;
};

function mergeTenantLists(
  preferred: BootstrapTenant[],
  fallback: BootstrapTenant[]
): BootstrapTenant[] {
  const merged = new Map<string, BootstrapTenant>();

  for (const tenant of fallback) {
    merged.set(tenant.id, tenant);
  }

  for (const tenant of preferred) {
    const existing = merged.get(tenant.id);
    if (!existing) {
      merged.set(tenant.id, tenant);
      continue;
    }

    merged.set(tenant.id, {
      ...existing,
      ...tenant,
      settings: tenant.settings ?? existing.settings,
      isActive: tenant.isActive ?? existing.isActive,
    });
  }

  return Array.from(merged.values());
}

async function enrichTenantSettings(tenantList: BootstrapTenant[]): Promise<BootstrapTenant[]> {
  if (tenantList.length === 0) {
    return tenantList;
  }

  const detailResults = await Promise.allSettled(
    tenantList.map(async tenant => {
      if (tenant.settings) {
        return tenant;
      }

      const detailedTenant = await api.getTenant(tenant.id);
      return {
        id: detailedTenant.id,
        name: detailedTenant.name,
        isActive: detailedTenant.isActive,
        settings: detailedTenant.settings,
      } satisfies BootstrapTenant;
    })
  );

  return tenantList.map((tenant, index) => {
    const result = detailResults[index];
    if (result.status === 'fulfilled') {
      return {
        ...tenant,
        ...result.value,
        settings: result.value.settings ?? tenant.settings,
      };
    }

    return tenant;
  });
}

async function ensureTenantBootstrap(user: AuthUser, token: string | null): Promise<void> {
  const key = getBootstrapKey(user, token);

  if (completedBootstrapKey === key) {
    return;
  }

  if (inFlightBootstrap?.key === key) {
    return inFlightBootstrap.promise;
  }

  const promise = bootstrapTenantContext(user)
    .then(() => {
      completedBootstrapKey = key;
    })
    .finally(() => {
      if (inFlightBootstrap?.key === key) {
        inFlightBootstrap = null;
      }
    });

  inFlightBootstrap = { key, promise };
  return promise;
}

async function bootstrapTenantContext(user: AuthUser) {
  const {
    mode,
    activeTenantId,
    tenantList: existingTenantList,
    hydrateTenantContext,
  } = useTenantStore.getState();
  const isAdmin = user.role === 'admin' || user.role === 'super_admin';

  const membershipTenantList = (user.tenantMemberships ?? []).map(membership => ({
    id: membership.tenantId,
    name: membership.tenantId,
    isActive: true,
  }));

  const membershipPrimaryTenantId =
    user.tenantMemberships?.find(membership => membership.isPrimary)?.tenantId ??
    user.tenantMemberships?.[0]?.tenantId ??
    null;

  if (isAdmin) {
    const shouldPreserveAllTenantsSelection = mode === 'all';
    const initialTenantList = mergeTenantLists(existingTenantList, membershipTenantList);

    // Guard: when admin selected "All tenants", keep preferred tenant null.
    // Forcing a tenant ID here re-scopes entity endpoints and can reintroduce
    // noisy forbidden probes during bootstrap.
    const initialPreferredTenantId = shouldPreserveAllTenantsSelection
      ? null
      : initialTenantList.some(tenant => tenant.id === activeTenantId)
        ? activeTenantId
        : (membershipPrimaryTenantId ??
          initialTenantList.find(tenant => tenant.isActive !== false)?.id ??
          initialTenantList[0]?.id ??
          null);

    if (initialTenantList.length > 0) {
      hydrateTenantContext({
        role: user.role,
        tenantList: initialTenantList,
        preferredTenantId: initialPreferredTenantId,
      });
    }

    if (!initialPreferredTenantId && initialTenantList.length === 0) {
      hydrateTenantContext({
        role: user.role,
        tenantList: [],
      });
      return;
    }

    let tenantList: BootstrapTenant[] = [];

    try {
      const tenantsResult = await api.listTenants({
        page: 1,
        pageSize: 100,
        isActive: true,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      if (tenantsResult.data.length > 0) {
        tenantList = mergeTenantLists(tenantsResult.data, tenantList);
      }
    } catch {
      // Fall back to membership-derived list if tenant listing is blocked
    }

    if (tenantList.length === 0 && membershipTenantList.length > 0) {
      tenantList = membershipTenantList;
    }

    tenantList = mergeTenantLists(tenantList, initialTenantList);
    tenantList = await enrichTenantSettings(tenantList);

    const preferredTenantId = shouldPreserveAllTenantsSelection
      ? null
      : tenantList.some(tenant => tenant.id === activeTenantId)
        ? activeTenantId
        : (membershipPrimaryTenantId ??
          tenantList.find(tenant => tenant.isActive !== false)?.id ??
          tenantList[0]?.id ??
          null);

    hydrateTenantContext({
      role: user.role,
      tenantList,
      preferredTenantId,
    });

    return;
  }

  const memberships = await api.listUserTenants(user.id);
  const activeMemberships = memberships.filter(membership => membership.tenant.isActive !== false);
  const staffTenantList = activeMemberships.map(membership => ({
    id: membership.tenant.id,
    name: membership.tenant.name,
    isActive: membership.tenant.isActive,
    settings: membership.tenant.settings,
  }));
  const hydratedStaffTenantList = await enrichTenantSettings(staffTenantList);

  const assignedTenantId =
    activeMemberships.find(membership => membership.isPrimary)?.tenantId ??
    activeMemberships[0]?.tenantId ??
    null;

  hydrateTenantContext({
    role: user.role,
    assignedTenantId,
    tenantList: hydratedStaffTenantList,
  });
}

/**
 * Hook for accessing auth state and actions
 */
export function useAuth() {
  const { token, user, isAuthenticated, isLoading, setAuth, clearAuth, checkTokenExpiry } =
    useAuthStore();
  const resetTenantContext = useTenantStore(state => state.resetTenantContext);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || !user || !token) {
      return;
    }

    void ensureTenantBootstrap(user, token).catch(error => {
      console.error('[Auth] Failed to bootstrap tenant context:', error);
    });
  }, [isAuthenticated, user, token]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({
      email,
      password,
      turnstileToken,
    }: {
      email: string;
      password: string;
      turnstileToken?: string;
    }) => {
      const result = await api.login(email, password, turnstileToken);
      return result;
    },
    onSuccess: async data => {
      // Set token in API client
      api.setToken(data.token);

      // Store auth state
      const authUser = data.user as AuthUser;
      setAuth(data.token, authUser, data.expiresAt);

      try {
        await ensureTenantBootstrap(authUser, data.token);
      } catch (error) {
        console.error('[Auth] Failed to bootstrap tenant context after login:', error);
      }

      // Navigate to dashboard
      navigate({ to: '/' });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        await api.logout();
      } catch {
        // Ignore logout errors, we'll clear the token anyway
      }
    },
    onSettled: () => {
      // Always clear auth state
      api.setToken(null);
      clearAuth();
      resetBootstrapGuard();
      resetTenantContext();

      // Clear query cache
      queryClient.clear();

      // Navigate to login
      navigate({ to: '/login' });
    },
  });

  // Initialize API client token from store
  if (token && !api.token) {
    api.setToken(token);
  }

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isManager = user?.role === 'manager';
  const isUser = user?.role === 'user';
  const isStaff = isManager || isUser;

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    isAdmin,
    isManager,
    isUser,
    isStaff,

    login: (email: string, password: string, turnstileToken?: string) => {
      return loginMutation.mutateAsync({ email, password, turnstileToken });
    },

    logout: () => {
      return logoutMutation.mutate();
    },

    loginError: loginMutation.error as ApiError | null,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,

    checkTokenExpiry,
  };
}

/**
 * Hook to fetch current user (for refreshing user data)
 */
export function useCurrentUser() {
  const { token, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const user = await api.getCurrentUser();
      return user;
    },
    enabled: isAuthenticated && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}
